import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

// 独立入口(main / platform / tenant)的 i18n 由 App 加载;本组件在 App 之外渲染,自己保证资源已初始化。
import "@/shared/lib/i18n";
import { api } from "@/shared/api";
import { migratedLoginUrl } from "@/shared/lib/migratedLoginUrl";

import { usePartnerNotFound } from "./store";

/**
 * OEM D1:渠道子区下 code 不存在 / 渠道停用(/public/brand 回 404 PARTNER_NOT_FOUND)时,
 * 全站只渲染这一页 —— 不带任何品牌(无 logo、无产品名、无版权/备案),也不挂业务 App(不发任何业务请求)。
 *
 * 1002:渠道解约 / 账号改挂后,终端用户拿着老地址进来只看到这一页、不知道该去哪登录。页底给一个收起的「查新的登录地址」:
 * 输账号密码 → 后端在未知子区上**凭据通过才**回该账号当前的登录地址(PARTNER_NOT_FOUND + details loginUrl=…),
 * 凭据错与修复前同一个 404。这里不登录、不存任何 token,只把地址告诉用户。
 */
export function PartnerNotFoundPage() {
  const { t } = useTranslation("common");
  return (
    <main
      role="main"
      data-testid="partner-not-found"
      className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background px-6"
    >
      <p
        data-testid="partner-not-found-title"
        className="max-w-md text-center text-base leading-relaxed text-muted-foreground"
      >
        {t("partnerNotFound.title")}
      </p>
      <MovedAccountLookup />
    </main>
  );
}

function MovedAccountLookup() {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [credential, setCredential] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<{ url: string | null } | null>(null);

  if (!open) {
    return (
      <button
        type="button"
        data-testid="partner-not-found-lookup-open"
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        onClick={() => setOpen(true)}
      >
        {t("partnerNotFound.lookupOpen")}
      </button>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!credential.trim() || !password) return;
    setPending(true);
    setResult(null);
    try {
      await api.post("/auth/tenant/login", { credential: credential.trim(), password });
      // 未知子区上后端不会签会话;万一成功(配置漂移)也不在这里登录,只当没查到。
      setResult({ url: null });
    } catch (err) {
      setResult({ url: migratedLoginUrl(err) });
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      data-testid="partner-not-found-lookup"
      onSubmit={(e) => void submit(e)}
      className="w-full max-w-xs space-y-2"
    >
      <p className="text-xs text-muted-foreground">{t("partnerNotFound.lookupHint")}</p>
      <input
        aria-label={t("partnerNotFound.credential")}
        placeholder={t("partnerNotFound.credential")}
        autoComplete="username"
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        value={credential}
        onChange={(e) => setCredential(e.target.value)}
      />
      <input
        type="password"
        aria-label={t("partnerNotFound.password")}
        placeholder={t("partnerNotFound.password")}
        autoComplete="current-password"
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <button
        type="submit"
        disabled={pending || !credential.trim() || !password}
        className="h-9 w-full rounded-md bg-primary text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {t("partnerNotFound.lookupSubmit")}
      </button>
      {result &&
        (result.url ? (
          <p role="status" data-testid="partner-not-found-moved" className="text-sm">
            {t("partnerNotFound.moved")}{" "}
            <a
              href={`${result.url}/login`}
              className="font-medium text-primary underline underline-offset-2"
            >
              {new URL(result.url).host}
            </a>
          </p>
        ) : (
          <p role="status" className="text-xs text-muted-foreground">
            {t("partnerNotFound.notMoved")}
          </p>
        ))}
    </form>
  );
}

/** 入口外层包一层:标记置位(首屏或 SWR 刷新时发现)即切到中性提示页。 */
export function PartnerNotFoundGate({ children }: { children: ReactNode }) {
  const notFound = usePartnerNotFound();
  return notFound ? <PartnerNotFoundPage /> : <>{children}</>;
}
